import { useState } from "react";
import { setTopAchiever } from "../actions/studensts";

// Only these roles may flag a student as a Top Achiever inline, since checking
// it writes a fee onto the student's invoice record.
const ALLOWED_ROLES = ["admin", "invoice"];

const LABEL = "Top Achiever";
const FEE = "500.00";
const METHOD_LABELS = { cash: "Cash", bank_transfer: "Bank Transfer" };
const EMPTY_PAYMENT = { payment_method: "", payer_name: "", payer_phone: "" };

// Same rules as the server: method, payer name and a 7–15 digit phone.
const validatePayment = (p) => {
  if (p.payment_method !== "cash" && p.payment_method !== "bank_transfer") {
    return "Choose how the Top Achievers fee was paid.";
  }
  if (!p.payer_name.trim()) return "Payer full name is required.";
  if (!/^\+?\d{7,15}$/.test(p.payer_phone.replace(/[\s-]/g, ""))) {
    return "Enter a valid payer phone number.";
  }
  return "";
};

// askPayment: ticking first asks how the Top Achievers fee was paid (its own
// payment method, separate from the audit form's). Off on the Invoice page,
// whose own flow prices Top Achievers.
export default function TopAchieverField({
  studentId,
  initialValue,
  initialFee,
  askPayment = true,
  initialPaymentMethod,
  initialPayerName,
  initialPayerPhone,
  initialPaymentDate,
}) {
  const role = JSON.parse(localStorage.getItem("profile"))?.value?.[0]?.role;
  const canEdit = ALLOWED_ROLES.includes(role);

  const [checked, setChecked] = useState(Number(initialValue) === 1);
  const [fee, setFee] = useState(
    initialFee === null || initialFee === undefined ? "" : String(initialFee)
  );
  const [payment, setPayment] = useState({
    payment_method: initialPaymentMethod || "",
    payer_name: initialPayerName || "",
    payer_phone: initialPayerPhone || "",
    payment_date: initialPaymentDate || "",
  });
  // The payment form, open while ticking (or editing) and not yet saved.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (next, paymentToSend) => {
    setSaving(true);
    setError("");
    const res = await setTopAchiever(studentId, next ? "1" : "0", paymentToSend);
    setSaving(false);
    if (res && res.status === 200) {
      setChecked(next);
      setFee(res.data?.top_achievers_fee ?? (next ? FEE : "0.00"));
      if (paymentToSend || !next) {
        setPayment({
          payment_method: res.data?.top_achievers_payment_method || "",
          payer_name: res.data?.top_achievers_payer_name || "",
          payer_phone: res.data?.top_achievers_payer_phone || "",
          payment_date: (res.data?.top_achievers_payment_date || "").slice(0, 16),
        });
      }
      setDraft(null);
    } else {
      setError(res?.data?.ERROR || "Failed to save.");
    }
  };

  const handleToggle = (e) => {
    const next = e.target.checked;
    if (saving) return;
    setError("");
    if (next && askPayment) {
      setDraft({ ...EMPTY_PAYMENT });
      return;
    }
    save(next);
  };

  const handleSavePayment = () => {
    const message = validatePayment(draft);
    if (message) {
      setError(message);
      return;
    }
    save(true, {
      payment_method: draft.payment_method,
      payer_name: draft.payer_name.trim(),
      payer_phone: draft.payer_phone.replace(/[\s-]/g, ""),
    });
  };

  const setField = (name) => (e) => setDraft((d) => ({ ...d, [name]: e.target.value }));
  const radioName = `top_achievers_payment_${studentId}`;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center flex-wrap gap-2">
        <label className="font-light" htmlFor={`top_achievers_${studentId}`}>
          {LABEL}:&nbsp;
        </label>

        {canEdit ? (
          <input
            id={`top_achievers_${studentId}`}
            type="checkbox"
            checked={checked || draft !== null}
            disabled={saving}
            onChange={draft !== null ? () => { setDraft(null); setError(""); } : handleToggle}
            className="w-4 h-4 accent-yellow-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          />
        ) : (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              checked ? "bg-yellow-200 text-yellow-900" : "bg-gray-100 text-gray-500"
            }`}
          >
            {checked ? "Yes" : "No"}
          </span>
        )}

        {checked && (
          <span className="text-xs font-semibold text-yellow-800">
            Amount: {Number(fee || FEE).toFixed(2)}
          </span>
        )}

        {checked && askPayment && payment.payment_method && draft === null && (
          <span className="text-xs text-gray-600">
            Paid by {METHOD_LABELS[payment.payment_method]}
            {payment.payment_date ? ` on ${payment.payment_date}` : ""} · {payment.payer_name} ·{" "}
            {payment.payer_phone}
            {canEdit && (
              <button
                type="button"
                onClick={() => { setDraft({ ...payment }); setError(""); }}
                className="ml-2 text-blue-600 underline"
              >
                Edit
              </button>
            )}
          </span>
        )}

        {checked && askPayment && !payment.payment_method && draft === null && canEdit && (
          <button
            type="button"
            onClick={() => { setDraft({ ...EMPTY_PAYMENT }); setError(""); }}
            className="text-xs text-blue-600 underline"
          >
            Add payment details
          </button>
        )}

        {saving && <span className="text-xs text-gray-400">Saving...</span>}
      </div>

      {draft !== null && (
        <div className="ml-1 p-3 border border-yellow-300 rounded-lg bg-yellow-50 flex flex-col gap-2 max-w-xl">
          <span className="text-sm font-semibold text-yellow-900">
            Top Achievers Payment Method
          </span>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={radioName}
                value="cash"
                checked={draft.payment_method === "cash"}
                onChange={setField("payment_method")}
              />
              Cash
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={radioName}
                value="bank_transfer"
                checked={draft.payment_method === "bank_transfer"}
                onChange={setField("payment_method")}
              />
              Bank Transfer
            </label>
          </div>
          <input
            type="text"
            placeholder="Payer full name"
            value={draft.payer_name}
            onChange={setField("payer_name")}
            maxLength={255}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            type="tel"
            placeholder="Payer phone number"
            value={draft.payer_phone}
            onChange={setField("payer_phone")}
            maxLength={20}
            className="border rounded px-2 py-1 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={handleSavePayment}
              className="px-3 py-1 rounded bg-yellow-500 text-white text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => { setDraft(null); setError(""); }}
              className="px-3 py-1 rounded border text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}
